import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

const ICONS = ['🌧️','🌦️','🌈','🌙','🌲','☕','🍃','🌊','🔥','❄️','⚡','🎧','🎵','🌸','🦋','🌌','🏔️','🌾','🍂','🎹']
const PARTICLES = [
  { value: 'none', label: '无' },
  { value: 'rain', label: '雨' },
  { value: 'fireflies', label: '萤火虫' },
  { value: 'snow', label: '雪花' },
  { value: 'stars', label: '星空' },
]
const MUSIC_SOURCES = [
  { label: '雨夜 · Rain', value: '/api/recommendations/rain', params: { limit: 20 } },
  { label: '咖啡馆 · Cafe', value: '/api/recommendations/cafe', params: { limit: 20 } },
  { label: '森林 · Forest', value: '/api/recommendations/forest', params: { limit: 20 } },
  { label: '海浪 · Ocean', value: '/api/recommendations/ocean', params: { limit: 20 } },
  { label: '赛博朋克 · Cyberpunk', value: '/api/recommendations/cyberpunk', params: { limit: 20 } },
  { label: '自然 · Nature', value: '/api/recommendations/nature', params: { limit: 20 } },
  { label: '古典 · Classical', value: '/api/recommendations/classical', params: { limit: 20 } },
]

const API = '' // use relative URL

function getToken() {
  return localStorage.getItem('fm_token')
}

function authHeaders() {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── helpers ──────────────────────────────────────────────────────

function SectionCard({ title, children }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 16,
      padding: '20px 24px',
      marginBottom: 16,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(180,200,255,0.6)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function FieldRow({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: 'rgba(180,200,255,0.5)', marginBottom: 6, letterSpacing: '0.03em' }}>{label}</div>
      {children}
    </div>
  )
}

function Input({ value, onChange, placeholder, style, ...rest }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 10,
        color: '#e8f0ff',
        fontSize: 14,
        padding: '9px 14px',
        outline: 'none',
        boxSizing: 'border-box',
        ...style,
      }}
      {...rest}
    />
  )
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 10,
        color: '#e8f0ff',
        fontSize: 14,
        padding: '9px 14px',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
      }}
    />
  )
}

function Button({ children, onClick, variant = 'default', disabled, style: extra }) {
  const base = {
    padding: '9px 20px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    opacity: disabled ? 0.4 : 1,
    border: 'none',
    ...extra,
  }
  if (variant === 'primary') {
    base.background = 'rgba(99,102,241,0.8)'
    base.color = '#fff'
    base.backdropFilter = 'blur(8px)'
  } else if (variant === 'danger') {
    base.background = 'rgba(239,68,68,0.2)'
    base.color = '#fca5a5'
    base.border = '1px solid rgba(239,68,68,0.3)'
  } else {
    base.background = 'rgba(255,255,255,0.07)'
    base.color = 'rgba(200,220,255,0.8)'
    base.border = '1px solid rgba(255,255,255,0.12)'
  }
  return <button onClick={disabled ? undefined : onClick} style={base} disabled={disabled}>{children}</button>
}

function SmallButton({ children, onClick, variant = 'ghost' }) {
  const styles = {
    ghost: { background: 'none', border: 'none', color: 'rgba(180,200,255,0.5)', cursor: 'pointer', fontSize: 12, padding: '2px 6px' },
    upload: { background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', borderRadius: 8, color: 'rgba(150,170,255,0.9)', cursor: 'pointer', fontSize: 12, padding: '5px 12px' },
  }
  return <button onClick={onClick} style={styles[variant]}>{children}</button>
}

// ── Icon picker ──────────────────────────────────────────────────

function IconPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {ICONS.map(icon => (
        <div
          key={icon}
          onClick={() => onChange(icon)}
          style={{
            width: 40, height: 40,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
            borderRadius: 10,
            cursor: 'pointer',
            background: value === icon ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.05)',
            border: value === icon ? '1.5px solid rgba(99,102,241,0.7)' : '1px solid rgba(255,255,255,0.1)',
            transition: 'all 0.15s',
          }}
        >
          {icon}
        </div>
      ))}
    </div>
  )
}

// ── Image item editor ────────────────────────────────────────────

function ImageItemEditor({ item, index: _index, onChange, onRemove }) {
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  async function handleFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API}/api/ambient-scenes/upload/image`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      })
      const data = await res.json()
      if (data.url) onChange({ ...item, url: data.url })
    } catch (err) {
      console.error('Upload failed', err)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
      <input
        value={item.url}
        onChange={e => onChange({ ...item, url: e.target.value })}
        placeholder="图片 URL 或上传文件"
        style={{
          flex: 1,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8,
          color: '#e8f0ff',
          fontSize: 13,
          padding: '7px 12px',
          outline: 'none',
        }}
      />
      <SmallButton variant="upload" onClick={() => fileInputRef.current?.click()}>
        {uploading ? '上传中…' : '上传'}
      </SmallButton>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
      <input
        type="number"
        value={item.interval / 1000}
        onChange={e => onChange({ ...item, interval: parseInt(e.target.value || 8) * 1000 })}
        title="轮播间隔（秒）"
        style={{
          width: 52,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8,
          color: '#e8f0ff',
          fontSize: 13,
          padding: '7px 8px',
          outline: 'none',
          textAlign: 'center',
        }}
      />
      <span style={{ fontSize: 11, color: 'rgba(150,170,210,0.5)', whiteSpace: 'nowrap' }}>秒/张</span>
      <button onClick={onRemove} style={{ background: 'none', border: 'none', color: 'rgba(239,68,68,0.6)', cursor: 'pointer', fontSize: 16, padding: '4px' }}>×</button>
    </div>
  )
}

// ── Sound item editor ───────────────────────────────────────────

function SoundItemEditor({ item, index: _index, onChange, onRemove }) {
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  async function handleFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API}/api/ambient-scenes/upload/sound`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      })
      const data = await res.json()
      if (data.url) onChange({ ...item, url: data.url })
    } catch (err) {
      console.error('Upload failed', err)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
      <input
        value={item.url}
        onChange={e => onChange({ ...item, url: e.target.value })}
        placeholder="音频 URL 或上传文件"
        style={{
          flex: 1,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8,
          color: '#e8f0ff',
          fontSize: 13,
          padding: '7px 12px',
          outline: 'none',
        }}
      />
      <SmallButton variant="upload" onClick={() => fileInputRef.current?.click()}>
        {uploading ? '上传中…' : '上传'}
      </SmallButton>
      <input ref={fileInputRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={handleFileUpload} />
      <input
        type="range"
        min={0} max={1} step={0.01}
        value={item.volume}
        onChange={e => onChange({ ...item, volume: parseFloat(e.target.value) })}
        style={{ width: 80 }}
        title={`音量 ${Math.round(item.volume * 100)}%`}
      />
      <span style={{ fontSize: 11, color: 'rgba(150,170,210,0.5)', width: 36, textAlign: 'right' }}>
        {Math.round(item.volume * 100)}%
      </span>
      <button onClick={onRemove} style={{ background: 'none', border: 'none', color: 'rgba(239,68,68,0.6)', cursor: 'pointer', fontSize: 16, padding: '4px' }}>×</button>
    </div>
  )
}

// ── Preview thumbnail ────────────────────────────────────────────

function PreviewThumb({ images }) {
  if (!images || images.length === 0) {
    return (
      <div style={{
        width: '100%',
        aspectRatio: '16/9',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 32,
      }}>
        🖼️
      </div>
    )
  }
  return (
    <img
      src={images[0].url}
      alt="封面预览"
      style={{
        width: '100%',
        aspectRatio: '16/9',
        objectFit: 'cover',
        borderRadius: 12,
        filter: 'brightness(0.7)',
      }}
      onError={e => { e.target.style.display = 'none' }}
    />
  )
}

// ── Main editor ──────────────────────────────────────────────────

export default function SceneEditor() {
  const { id } = useParams()  // id = sceneId for edit mode
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const [error, setError] = useState(null)

  // Form state
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon] = useState('🌧️')
  const [bgImages, setBgImages] = useState([{ url: '', interval: 8000 }])
  const [particle, setParticle] = useState('none')
  const [bgSounds, setBgSounds] = useState([{ url: '', volume: 0.15 }])
  const [soundMode, setSoundMode] = useState('simultaneous')
  const [soundRotateInterval, setSoundRotateInterval] = useState(15000)
  const [musicApi, setMusicApi] = useState('/api/recommendations/rain')
  const [musicParams, setMusicParams] = useState({ limit: 20 })

  // Load existing scene if editing
  useEffect(() => {
    if (!isEdit) return
    async function load() {
      try {
        const res = await fetch(`${API}/api/ambient-scenes/${id}`)
        if (!res.ok) throw new Error('场景不存在')
        const scene = await res.json()
        setName(scene.name || '')
        setNameEn(scene.name_en || '')
        setDescription(scene.description || '')
        setIcon(scene.icon || '🌧️')
        setBgImages(scene.bg_images?.length ? scene.bg_images : [{ url: '', interval: 8000 }])
        setParticle(scene.particle || 'none')
        setBgSounds(scene.bg_sounds?.length ? scene.bg_sounds : [{ url: '', volume: 0.15 }])
        setSoundMode(scene.sound_mode || 'simultaneous')
        setSoundRotateInterval(scene.sound_rotate_interval || 15000)
        setMusicApi(scene.music_api || '/api/recommendations/rain')
        setMusicParams(scene.music_params || { limit: 20 })
      } catch (e) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, isEdit])

  // Redirect if not logged in
  useEffect(() => {
    if (!getToken()) {
      setError('请先登录后再创建场景')
    }
  }, [])

  function updateBgImage(i, patch) {
    setBgImages(prev => prev.map((img, idx) => idx === i ? { ...img, ...patch } : img))
  }
  function removeBgImage(i) {
    setBgImages(prev => prev.filter((_, idx) => idx !== i))
  }
  function addBgImage() {
    setBgImages(prev => [...prev, { url: '', interval: 8000 }])
  }

  function updateBgSound(i, patch) {
    setBgSounds(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s))
  }
  function removeBgSound(i) {
    setBgSounds(prev => prev.filter((_, idx) => idx !== i))
  }
  function addBgSound() {
    setBgSounds(prev => [...prev, { url: '', volume: 0.15 }])
  }

  async function handleSave() {
    if (!name.trim()) { setError('请输入场景名称'); return }
    const validImages = bgImages.filter(x => x.url.trim())
    if (validImages.length === 0) { setError('请至少添加一张背景图'); return }

    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: name.trim(),
        name_en: nameEn.trim() || null,
        description: description.trim() || null,
        icon,
        bg_images: validImages,
        particle,
        bg_sounds: bgSounds.filter(x => x.url.trim()),
        sound_mode: soundMode,
        sound_rotate_interval: soundRotateInterval,
        music_source_type: 'api',
        music_api: musicApi,
        music_params: musicParams,
      }

      const method = isEdit ? 'PUT' : 'POST'
      const url = isEdit
        ? `${API}/api/ambient-scenes/${id}`
        : `${API}/api/ambient-scenes`

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      })

      if (res.status === 401) { setError('请先登录'); return }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || '保存失败')
      }

      const saved = await res.json()
      navigate(`/scene/${saved.id}`)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a1a', color: '#e8f0ff' }}>
        加载中…
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a1a', color: '#e8f0ff', paddingBottom: 80 }}>
      <Helmet>
        <title>{isEdit ? '编辑场景' : '创建场景'} | FreeMusic.Top</title>
      </Helmet>

      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(10,10,26,0.9)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '16px 24px',
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10, color: 'rgba(200,220,255,0.8)',
            cursor: 'pointer', fontSize: 14, padding: '7px 16px',
          }}
        >
          ← 返回
        </button>
        <div style={{ fontSize: 17, fontWeight: 600, color: '#e8f0ff', flex: 1 }}>
          {isEdit ? '编辑场景' : '创建新场景'}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button onClick={handleSave} variant="primary" disabled={saving}>
            {saving ? '保存中…' : '保存'}
          </Button>
          {isEdit && (
            <Button onClick={() => navigate(`/ambient/${id}`)}>
              预览 →
            </Button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px' }}>

        {/* Error banner */}
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.15)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 20,
            fontSize: 13,
            color: '#fca5a5',
          }}>
            {error}
          </div>
        )}

        {/* Preview */}
        <div style={{ marginBottom: 24 }}>
          <PreviewThumb images={bgImages.filter(x => x.url)} />
        </div>

        {/* Basic info */}
        <SectionCard title="基本信息">
          <FieldRow label="场景名称 *">
            <Input value={name} onChange={setName} placeholder="例如：雨夜咖啡馆" maxLength={64} />
          </FieldRow>
          <FieldRow label="英文名称">
            <Input value={nameEn} onChange={setNameEn} placeholder="Rainy Night" maxLength={64} />
          </FieldRow>
          <FieldRow label="描述">
            <Textarea value={description} onChange={setDescription} placeholder="简短描述这个场景的意境…" rows={2} maxLength={256} />
          </FieldRow>
          <FieldRow label="图标">
            <IconPicker value={icon} onChange={setIcon} />
          </FieldRow>
        </SectionCard>

        {/* Background images */}
        <SectionCard title="背景图（可多张轮播）">
          {bgImages.map((img, i) => (
            <ImageItemEditor
              key={i}
              item={img}
              index={i}
              onChange={(patch) => updateBgImage(i, patch)}
              onRemove={() => removeBgImage(i)}
            />
          ))}
          <Button onClick={addBgImage}>+ 添加背景图</Button>
        </SectionCard>

        {/* Particle effects */}
        <SectionCard title="粒子特效">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {PARTICLES.map(p => (
              <button
                key={p.value}
                onClick={() => setParticle(p.value)}
                style={{
                  padding: '7px 16px',
                  borderRadius: 10,
                  fontSize: 13,
                  cursor: 'pointer',
                  background: particle === p.value ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.06)',
                  border: particle === p.value ? '1.5px solid rgba(99,102,241,0.7)' : '1px solid rgba(255,255,255,0.1)',
                  color: particle === p.value ? '#c7d2ff' : 'rgba(180,200,255,0.7)',
                  transition: 'all 0.15s',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </SectionCard>

        {/* Background sounds */}
        <SectionCard title="背景音效">
          <div style={{ marginBottom: 12 }}>
            {bgSounds.map((s, i) => (
              <SoundItemEditor
                key={i}
                item={s}
                index={i}
                onChange={(patch) => updateBgSound(i, patch)}
                onRemove={() => removeBgSound(i)}
              />
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
            <Button onClick={addBgSound}>+ 添加音效</Button>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'rgba(180,200,255,0.5)' }}>播放模式：</span>
              {['simultaneous', 'rotate'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setSoundMode(mode)}
                  style={{
                    padding: '5px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                    background: soundMode === mode ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
                    border: soundMode === mode ? '1px solid rgba(99,102,241,0.6)' : '1px solid rgba(255,255,255,0.1)',
                    color: soundMode === mode ? '#c7d2ff' : 'rgba(180,200,255,0.6)',
                  }}
                >
                  {mode === 'simultaneous' ? '🔊 同时播放' : '🔁 轮流播放'}
                </button>
              ))}
            </div>
            {soundMode === 'rotate' && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'rgba(180,200,255,0.5)' }}>切换间隔：</span>
                <input
                  type="number"
                  value={soundRotateInterval / 1000}
                  onChange={e => setSoundRotateInterval(parseInt(e.target.value || 15) * 1000)}
                  min={5} max={120}
                  style={{
                    width: 52, background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
                    color: '#e8f0ff', fontSize: 13, padding: '5px 8px', outline: 'none', textAlign: 'center',
                  }}
                />
                <span style={{ fontSize: 12, color: 'rgba(180,200,255,0.5)' }}>秒</span>
              </div>
            )}
          </div>
        </SectionCard>

        {/* Music source */}
        <SectionCard title="背景音乐">
          <FieldRow label="音乐来源">
            <select
              value={musicApi}
              onChange={e => setMusicApi(e.target.value)}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
                color: '#e8f0ff',
                fontSize: 14,
                padding: '9px 14px',
                outline: 'none',
                appearance: 'none',
              }}
            >
              {MUSIC_SOURCES.map(s => (
                <option key={s.value} value={s.value} style={{ background: '#1a1a2e' }}>{s.label}</option>
              ))}
            </select>
          </FieldRow>
          <div style={{ fontSize: 12, color: 'rgba(150,170,210,0.4)', marginTop: 4 }}>
            音乐将自动从 freemusic.top 曲库加载，支持 {MUSIC_SOURCES.length} 种氛围分类
          </div>
        </SectionCard>

        {/* Save */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
          <Button onClick={() => navigate(-1)}>取消</Button>
          <Button onClick={handleSave} variant="primary" disabled={saving}>
            {saving ? '保存中…' : (isEdit ? '保存修改' : '创建场景')}
          </Button>
        </div>
      </div>
    </div>
  )
}
